// ----------------------------------------------------------------------
// <copyright file="Izenda.LoadingManager.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * LoadingManager
 */
!(function() {
  var m = require('mobx');

  const {
    size,
    findIndex,
    nth,
    forEach,
    getOr,
    reduce,
    invoke,
    concat,
    map,
    curry,
    last,
    has,
    head,
    tail,
    keys,
    get
  } = require('lodash/fp');

  // save all loading instance and fetchings which are linked to loading instance
  var loadingItems = [];

  // save fetchings which do not link to any loading instance (global loading)
  var anonymousFetching = {
    fetchingPromise: [],
    loadingConfig: m.observable({
      allowToCancel: null
    })
  };

  /**
   * Add loading instance >> a loading-indicate will be showed
   * @param {PageService} loadingInstance
   */
  function addLoadingWatcher(loadingInstance) {
    var foundIndex = findLoadingIndex(loadingInstance);

    if (foundIndex >= 0) {
      throw new Error('loading instance is watched');
    }

    loadingItems.push({
      instance: loadingInstance,
      fetchingPromise: [],
      loadingConfig: m.observable({
        allowToCancel: null
      })
    });
  }

  /**
   * Remove loading instance >> a loading-indicate will be destroyed
   * @param  {PageService} loadingInstance
   * @return {undefined}
   */
  function removeLoadingWatcher(loadingInstance) {
    var foundIndex = findLoadingIndex(loadingInstance);

    if (foundIndex < 0) {
      throw new Error('loading instance is not added');
    }

    if (!size(loadingItems[foundIndex].fetchingPromise)) {
      anonymousFetching.fetchingPromise = [];
      anonymousFetching.loadingConfig.allowToCancel = null;
    }

    loadingItems.splice(foundIndex, 1);
  }

  /**
   * find loading instance and return its index
   * @param  {PageService} loadingInstance
   * @return {number}
   */
  function findLoadingIndex(loadingInstance) {
    // use function instead of object to prevent lodash use isEqual
    return findIndex(function(item) {
      return item.instance === loadingInstance;
    }, loadingItems);
  }

  /**
   * find loading instance and return its info (if any)
   * @param  {PageService} loadingInstance
   * @return {object}
   */
  function findLoadingInfo(loadingInstance) {
    var index = findLoadingIndex(loadingInstance);
    return index < 0 ? null : nth(index, loadingItems);
  }

  /**
   * Loading-indicator is cancelled
   * Reject all fetchings link to cancelled-loading
   * If dont have any fechings link to cancelled-loading, reject all fetchings in anonymousFetching
   * @param  {PageService} loadingInstance
   * @return {undefined}
   */
  function rejectLoading(loadingInstance) {
    var foundIndex = findLoadingIndex(loadingInstance);

    if (foundIndex < 0) {
      // reject all watched-loading
      forEach(reject, loadingItems);
    }

    var found = nth(foundIndex, loadingItems);

    reject(found);

    function reject(loadingItem) {
      // that loading item do not have fetching promise >> reject the anonymous fetching
      if (!size(loadingItem.fetchingPromise)) {
        loadingItem = anonymousFetching;
      }

      var promises = loadingItem.fetchingPromise;

      // reject promise
      forEach(function(promise) {
        invoke('cancel', promise);
      }, promises);

      // remove rejected-promise
      promises.splice(0, promises.length);

      // reset config to default values
      loadingItem.loadingConfig.allowToCancel = null;
    }
  }

  /**
   * Check loading instance is showed or not
   * @param  {PageService} loadingInstance
   * @return {boolean}
   */
  function checkShowLoading(loadingInstance) {
    var found = findLoadingIndex(loadingInstance);

    if (found < 0) {
      return reduce(
        function(re, loading) {
          return re || isShow(loading);
        },
        false,
        loadingItems
      );
    }

    return isShow(nth(found, loadingItems));

    function isShow(loading) {
      return !!getOr(0, 'loadingCounter', loading.instance);
    }
  }

  /**
   * check whether to cancel loading
   * @param  {PageService} loadingInstance
   * @return {boolean}
   */
  function allowToCancelProgress(loadingInstance) {
    var index = findLoadingIndex(loadingInstance);

    if (index < 0) {
      return false;
    }

    var found = nth(index, loadingItems);
    var sizeItemFetching = size(found.fetchingPromise);
    var sizeAnonymousFetching = size(anonymousFetching.fetchingPromise);

    var allowCancelItemFetching = allowToCancel(found);
    var allowCancelAnonymous = allowToCancel(anonymousFetching);

    if (sizeItemFetching > 0) {
      return allowCancelItemFetching;
    } else {
      return allowCancelAnonymous;
    }

    function allowToCancel(loading) {
      return getOr(false, 'allowToCancel', loading.loadingConfig);
    }
  }

  /**
   * isLoadingInstance description]
   * @param  {[type]}  loader [description]
   * @return {Boolean}        [description]
   */
  function isLoadingInstance(loader) {
    return has('loadingCounter', loader) && has('notifyLoadingChanged', loader);
  }

  /**
   * attach loading watcher
   * @return {Function}
   */
  function attachLoadingWatcher() {
    var args = arguments;
    var numArgs = size(args);
    var loadingDataFn = last(args);
    var loadingInfo = {};

    var fetchersCollection = [];
    var i = 0;
    for (i; i < numArgs - 1; i++) {
      fetchersCollection.push(args[i]);
    }

    return function() {
      var loadingInstance = head(arguments);
      var i = 0;

      if (!isLoadingInstance(loadingInstance)) {
        return loadingDataFn.apply(this, concat([null], fetchersCollection)).apply(this, arguments);
      }

      // build fetchers link to to loader
      var linkedLoaderFetchersColllection = map(curry(attachWacher)(loadingInstance), fetchersCollection);
      return loadingDataFn
        .apply(null, concat([loadingInstance], linkedLoaderFetchersColllection))
        .apply(this, tail(arguments));
    };

    function attachWacher(loadingInstance, fetchers) {
      var fetcherNames = keys(fetchers);

      var newFetchers = {};

      forEach(function(fetcherName) {
        newFetchers[fetcherName] = function() {
          return fetchers[fetcherName].apply(fetchers, concat(loadingInstance, arguments));
        };
      }, fetcherNames);
      return newFetchers;
    }
  }

  /**
   * attach loading ưatchers
   * @param  {Object} fetchers
   * @param  {Object} config
   * @return {Object}
   */
  function attachLoadingWatchers(fetchers, config) {
    var fetcherNames = keys(fetchers);
    var sourceFetchers = fetchers;

    forEach(curry(addWatcher)(sourceFetchers), fetcherNames);

    return sourceFetchers;

    function addWatcher(source, name) {
      var oldFetcher = fetchers[name].bind(fetchers);

      source[name] = function() {
        var loadingInstance, loadingInfo, args;

        loadingInstance = head(arguments);

        if (isLoadingInstance(loadingInstance)) {
          loadingInfo = findLoadingInfo(loadingInstance);
          args = tail(arguments);
        } else {
          loadingInfo = anonymousFetching;
          args = arguments;
        }

        var isShowLoading = checkShowLoading(loadingInstance);

        if (!isShowLoading) {
          return oldFetcher.apply(null, args);
        }

        // if global loading is showed
        var loadingPromise = createFakePromise(oldFetcher, get(name, config), args);

        loadingInfo.fetchingPromise.push(loadingPromise);

        if (loadingInfo.loadingConfig.allowToCancel !== false) {
          // hack - to prevent observable value is changed while its is used to render UI
          setTimeout(function() {
            loadingInfo.loadingConfig.allowToCancel = getOr(true, 'allowToCancel', get(name, config));
          }, 0);
        }

        return loadingPromise;
      };
    }

    function createFakePromise(fnFetchingData, config, args) {
      var fakePromise = new Promise(function(resolve) {
        resolve(fnFetchingData.apply(null, args));
      });

      return fakePromise;
    }
  }

  module.exports = {
    // for fetcher & store
    linkToLoadingProgress: attachLoadingWatchers,
    addLoadingProgress: attachLoadingWatcher,

    // for loading progress
    addLoadingWatcher: addLoadingWatcher,
    removeLoadingWatcher: removeLoadingWatcher,
    cancelLoading: rejectLoading,
    allowToCancelGlobalProgress: allowToCancelProgress
  };
})();



// WEBPACK FOOTER //
// ./ui/modules/Izenda.LoadingManager.js